import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  useAddDocumentToFavouritesMutation, 
  useRemoveDocumentFromFavouritesMutation,
  dashboardApi
} from '@/pages/dashboard/dashboard.reducer';
import { updateDocumentCounts } from '@/pages/dashboard/dashboard.slice';
import useDocumentCountsManager from '@/hooks/useDocumentCountsManager';

export const useFavouritesManager = (options = {}) => {
  const dispatch = useDispatch();
  const currentSiteId = useSelector(state => state.dashboardPage.currentSiteId);
  const { refreshAllCounts } = useDocumentCountsManager();
  
  const {
    tableId = null,
    tableFilters = null,
    updateTotalDocumentCount = null,
    refetch = null,
    siteId = currentSiteId,
    onSuccess = null,
    onError = null,
    updateOnlyFavourites = true,
    preventRapidToggleMs = 450
  } = options;

  const pendingRef = useRef(new Map());

  const markPending = useCallback((ids) => {
    const now = Date.now();
    ids.forEach(id => {
      if (pendingRef.current.has(id)) {
        const existing = pendingRef.current.get(id);
        if (existing?.timeoutId) clearTimeout(existing.timeoutId);
      }
      const timeoutId = setTimeout(() => {
        const entry = pendingRef.current.get(id);
        if (entry && entry.expiresAt <= Date.now()) {
          pendingRef.current.delete(id);
        }
      }, preventRapidToggleMs + 50);
      pendingRef.current.set(id, { startedAt: now, expiresAt: now + preventRapidToggleMs, timeoutId });
    });
  }, [preventRapidToggleMs]);

  const releasePending = useCallback((ids) => {
    ids.forEach(id => {
      const entry = pendingRef.current.get(id);
      if (!entry) return;
      if (entry.timeoutId) clearTimeout(entry.timeoutId);
      setTimeout(() => {
        const current = pendingRef.current.get(id);
        if (current && current.startedAt === entry.startedAt) {
          pendingRef.current.delete(id);
        }
      }, preventRapidToggleMs);
    });
  }, [preventRapidToggleMs]);

  const isItemUpdating = useCallback((id) => pendingRef.current.has(id), []);

  const createFavouritesUpdater = useCallback((targetSiteId = siteId) => {
    return async () => {
      if (updateOnlyFavourites) {
        try {
          const result = await dispatch(
            dashboardApi.endpoints.getFavouriteDocumentsCount.initiate(targetSiteId)
          ).unwrap();
          dispatch(updateDocumentCounts({ favourites: result }));
          return result;
        } catch (error) {
          console.error('Failed to refresh favourites count:', error);
          throw error;
        }
      } else {
        return await refreshAllCounts(targetSiteId);
      }
    };
  }, [dispatch, siteId, updateOnlyFavourites, refreshAllCounts]);

  const efficientCountUpdater = (updateTotalDocumentCount && !updateOnlyFavourites) 
    ? updateTotalDocumentCount 
    : createFavouritesUpdater(siteId);

  const [addDocumentToFavourites, { isLoading: isAdding }] = useAddDocumentToFavouritesMutation();
  const [removeDocumentFromFavourites, { isLoading: isRemoving }] = useRemoveDocumentFromFavouritesMutation();

  const addToFavourites = useCallback(async (items, customOptions = {}) => {
    let itemsArray = Array.isArray(items) ? items : [items];
    const actionable = itemsArray.filter(id => !pendingRef.current.has(id));
    if (actionable.length === 0) {
      return { skipped: true, reason: 'pending' };
    }
    itemsArray = actionable;
    markPending(itemsArray);
    const options = { 
      items: itemsArray,
      siteId,
      tableId,
      tableFilters,
      updateTotalDocumentCount: efficientCountUpdater,
      refetch,
      ...customOptions 
    };

    try {
      const result = await addDocumentToFavourites(options).unwrap();
      
      if (onSuccess) {
        onSuccess('add', result);
      }
      
      releasePending(itemsArray);
      return result;
    } catch (error) {
      console.error('Failed to add to favourites:', error);
      
      if (onError) {
        onError('add', error);
      }
      releasePending(itemsArray);
      throw error;
    }
  }, [
    addDocumentToFavourites, 
    siteId, 
    tableId, 
    tableFilters, 
    efficientCountUpdater, 
    refetch, 
    onSuccess, 
    onError,
    markPending,
    releasePending
  ]);

  const removeFromFavourites = useCallback(async (items, customOptions = {}) => {
    let itemsArray = Array.isArray(items) ? items : [items];
    const actionable = itemsArray.filter(id => !pendingRef.current.has(id));
    if (actionable.length === 0) {
      return { skipped: true, reason: 'pending' };
    }
    itemsArray = actionable;
    markPending(itemsArray);
    const options = { 
      items: itemsArray,
      siteId,
      tableId,
      tableFilters,
      updateTotalDocumentCount: efficientCountUpdater,
      refetch,
      ...customOptions 
    };

    try {
      const result = await removeDocumentFromFavourites(options).unwrap();
      
      if (onSuccess) {
        onSuccess('remove', result);
      }
      
      releasePending(itemsArray);
      return result;
    } catch (error) {
      console.error('Failed to remove from favourites:', error);
      
      if (onError) {
        onError('remove', error);
      }
      releasePending(itemsArray);
      throw error;
    }
  }, [
    removeDocumentFromFavourites, 
    siteId, 
    tableId, 
    tableFilters, 
    efficientCountUpdater, 
    refetch, 
    onSuccess, 
    onError,
    markPending,
    releasePending
  ]);

  const toggleFavourite = useCallback(async (item, customOptions = {}) => {
    const id = item.documentId;
    if (pendingRef.current.has(id)) {
      return { skipped: true, reason: 'pending' };
    }
    if (item.favourite) {
      return await removeFromFavourites(id, customOptions);
    } else {
      return await addToFavourites(id, customOptions);
    }
  }, [addToFavourites, removeFromFavourites]);


  const batchToggleFavourites = useCallback(async (items, favouriteState, customOptions = {}) => {
    const documentIds = items.map(item => item.documentId);
    
    if (favouriteState) {
      return await addToFavourites(documentIds, customOptions);
    } else {
      return await removeFromFavourites(documentIds, customOptions);
    }
  }, [addToFavourites, removeFromFavourites]);

  return {
    addToFavourites,
    removeFromFavourites,
    toggleFavourite,
    batchToggleFavourites,
    isAdding,
    isRemoving,
    isLoading: isAdding || isRemoving,
    isItemUpdating
  };
};

export default useFavouritesManager;
